import { Dispatch, SetStateAction } from 'react';
import { ace_index } from '@axo/shared/data-access/types';
import { FilterSelect } from '../FilterSelect';
import { setPageNumber } from '../SearchResults';

interface Props {
  query: ace_index.FindInput;
  setQuery: Dispatch<SetStateAction<ace_index.FindInput>>;
  editable?: boolean;
}

/** Single-select: at most ONE action field is active at a time. Each option
 * targets its own boolean field on FindInput. Re-selecting the active option
 * toggles it off, clearing the filter (the implicit "no filter" / all state). */
type ActionKey = 'followup' | 'userFollowup';
type ActionField = 'CommentHasFollowupAt' | 'UserCommentHasFollowupAt';

export interface ActionOption {
  key: ActionKey;
  label: string;
  field: ActionField;
}

// eslint-disable-next-line react-refresh/only-export-components
export const actionOptions: ActionOption[] = [
  { key: 'followup', label: 'Follow-up', field: 'CommentHasFollowupAt' },
  {
    key: 'userFollowup',
    label: 'Follow-Up by User',
    field: 'UserCommentHasFollowupAt',
  },
];

// The boolean fields cleared/set by this filter — derived from actionOptions so
// a new option automatically keeps selectAction's sibling-clearing in sync.
const ACTION_FIELDS: ActionField[] = actionOptions.map(
  (option) => option.field
);

// eslint-disable-next-line react-refresh/only-export-components
export function getSelectedAction(
  query: ace_index.FindInput
): ActionOption | undefined {
  return actionOptions.find((option) => query[option.field] === true);
}

// eslint-disable-next-line react-refresh/only-export-components
export function selectAction(
  option: ActionOption
): (query: ace_index.FindInput) => ace_index.FindInput {
  return (query) => {
    // Clear every action field first, then set the chosen one — so only a
    // single action is ever active. Re-selecting the active option leaves
    // everything cleared, which is the implicit "no filter" state.
    const wasActive = query[option.field] === true;
    const cleared: ace_index.FindInput = { ...setPageNumber(1)(query) };
    for (const field of ACTION_FIELDS) cleared[field] = undefined;
    if (!wasActive) cleared[option.field] = true;
    return cleared;
  };
}

export function ActionNeeded({ query, setQuery, editable = false }: Props) {
  const value = getSelectedAction(query);

  return (
    <FilterSelect
      items={actionOptions}
      value={value}
      onSelect={(option) => setQuery(selectAction(option))}
      stringify={(option) => option.label}
      label="Actions needed"
      description="Filter whether the application requires action"
      disabled={!editable}
    />
  );
}
